import type { KeyValueStore } from './kvStore';
import type { StorageChangeListener, StorageChangeMap, Unsubscribe } from './types';

/**
 * IndexedDB implementation of the backend key-value store for the web/PWA
 * target (ADR-012). Mirrors `chrome.storage.local` semantics, including
 * change events: every mutation computes {oldValue, newValue} per key and
 * emits it to in-page subscribers plus a BroadcastChannel so other tabs of
 * the app observe changes the way other extension surfaces do.
 */

const DB_NAME = 'top-prompt-library';
const DB_VERSION = 1;
const STORE = 'kv';
const CHANGES_CHANNEL = 'tpl-storage-changes';

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(STORE)) {
          request.result.createObjectStore(STORE);
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error('Failed to open IndexedDB'));
    });
  }
  return dbPromise;
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onabort = tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
  });
}

// ---- Change events ---------------------------------------------------------

const listeners = new Set<StorageChangeListener>();
const channel =
  typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(CHANGES_CHANNEL) : null;

if (channel) {
  channel.onmessage = event => {
    const changes = event.data as StorageChangeMap;
    listeners.forEach(listener => {
      try {
        listener(changes);
      } catch {
        // One faulty listener must not break the others.
      }
    });
  };
}

function emitChanges(changes: StorageChangeMap): void {
  if (Object.keys(changes).length === 0) return;
  listeners.forEach(listener => {
    try {
      listener(changes);
    } catch {
      // One faulty listener must not break the others.
    }
  });
  channel?.postMessage(changes);
}

/** Subscribe to this store's change events (used by the web platform adapter). */
export function onWebStorageChanged(listener: StorageChangeListener): Unsubscribe {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// ---- Store operations ------------------------------------------------------

export const webKvStore: KeyValueStore = {
  available: typeof indexedDB !== 'undefined',

  async get(keys) {
    const db = await openDb();
    const tx = db.transaction(STORE, 'readonly');
    const store = tx.objectStore(STORE);
    const result: Record<string, any> = {};
    if (keys === null) {
      const [allKeys, allValues] = await Promise.all([
        requestToPromise(store.getAllKeys()),
        requestToPromise(store.getAll()),
      ]);
      allKeys.forEach((key, i) => {
        result[String(key)] = allValues[i];
      });
    } else {
      const list = Array.isArray(keys) ? keys : [keys];
      const values = await Promise.all(list.map(key => requestToPromise(store.get(key))));
      list.forEach((key, i) => {
        if (values[i] !== undefined) result[key] = values[i];
      });
    }
    await txDone(tx);
    return result;
  },

  async set(items) {
    const entries = Object.entries(items);
    if (entries.length === 0) return;
    const db = await openDb();
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    // Read old values inside the same transaction so the change events carry
    // accurate {oldValue, newValue} pairs like chrome.storage.onChanged.
    const oldValues = await Promise.all(entries.map(([key]) => requestToPromise(store.get(key))));
    entries.forEach(([key, value]) => store.put(value, key));
    await txDone(tx);
    const changes: StorageChangeMap = {};
    entries.forEach(([key, value], i) => {
      changes[key] = { oldValue: oldValues[i], newValue: value };
    });
    emitChanges(changes);
  },

  async remove(keys) {
    const list = Array.isArray(keys) ? keys : [keys];
    if (list.length === 0) return;
    const db = await openDb();
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    const oldValues = await Promise.all(list.map(key => requestToPromise(store.get(key))));
    list.forEach(key => store.delete(key));
    await txDone(tx);
    const changes: StorageChangeMap = {};
    list.forEach((key, i) => {
      if (oldValues[i] !== undefined) {
        changes[key] = { oldValue: oldValues[i], newValue: undefined };
      }
    });
    emitChanges(changes);
  },

  async clear() {
    const db = await openDb();
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    const [allKeys, allValues] = await Promise.all([
      requestToPromise(store.getAllKeys()),
      requestToPromise(store.getAll()),
    ]);
    store.clear();
    await txDone(tx);
    const changes: StorageChangeMap = {};
    allKeys.forEach((key, i) => {
      changes[String(key)] = { oldValue: allValues[i], newValue: undefined };
    });
    emitChanges(changes);
  },
};
